import { z } from 'zod';
import { ChildInput, OrganisationInput, PractitionerInput, SignupPath } from './contracts';
/**
 * The funnel's resume state — the ONE documented exception to server-state-only
 * (architecture §6.5).
 *
 * The design requires a refresh to resume mid-flow, and there is no account to
 * persist against yet. So it is written down, and hedged four ways:
 *
 *   1. **The password is never stored.** It is held in React state for the life
 *      of the tab and posted once. A signup form that survives a refresh is a
 *      convenience; a password sitting in web storage is a liability, and the
 *      cost of re-typing it after a refresh is one field.
 *   2. **Versioned.** A shape change discards rather than crashes on a stale
 *      blob — a funnel that throws on load is worse than one that restarts.
 *   3. **Namespaced**, so it cannot collide with the token store or the theme.
 *   4. **Cleared on completion and on sign-in**, so a shared computer does not
 *      hand the next person a half-filled form with someone's email in it.
 */
const STORAGE_KEY = 'lb.web.funnel';
const VERSION = 1;
/**
 * Note what is absent: `password`. The type makes storing one impossible rather
 * than merely discouraged.
 */
export const FunnelState = z.object({
    version: z.literal(VERSION),
    path: SignupPath.optional(),
    planKey: z.string().optional(),
    fullName: z.string().optional(),
    email: z.string().optional(),
    relationship: z.string().optional(),
    marketingConsent: z.boolean().optional(),
    children: z.array(ChildInput).default([]),
    practitioner: PractitionerInput.partial().optional(),
    organisation: OrganisationInput.partial().optional(),
    promoCode: z.string().optional(),
    /** The capability, once a session exists. Never logged. */
    checkoutId: z.string().uuid().optional(),
});
export const EMPTY_FUNNEL = { version: VERSION, children: [] };
export function readFunnel() {
    try {
        // eslint-disable-next-line no-restricted-properties -- the documented owner of this key
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return EMPTY_FUNNEL;
        const parsed = FunnelState.safeParse(JSON.parse(raw));
        // A stale or tampered blob restarts the funnel rather than throwing.
        return parsed.success ? parsed.data : EMPTY_FUNNEL;
    }
    catch {
        return EMPTY_FUNNEL;
    }
}
export function writeFunnel(state) {
    try {
        // eslint-disable-next-line no-restricted-properties -- see above
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
    catch {
        // Private mode. The funnel still works for this tab; only resume is lost.
    }
}
export function clearFunnel() {
    try {
        // eslint-disable-next-line no-restricted-properties -- see above
        window.localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // Nothing to do — it was never written.
    }
}
//# sourceMappingURL=funnel-store.js.map